import cn from 'classnames';
import {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import SpinnerLoading from '../../../components/SpinnerLoading';
import usePatchBasket from '../../../services/hookService/patchBasket/usePatchBasket';
import usePostBasket from '../../../services/hookService/postBasket/usePostBasket';
import useDeleteBasket from '../../../services/hookService/deleteBasket/useDeleteBasket';

interface IProps {
  variant?: 'lg' | 'md' | 'sm';
  hideToast?: boolean;
  min_sell_quantity: number;
  available_quantity: number;
  hold_quantity: number;
  basket_quantity: number;
  basket_guid: string;
  advertisement_id: string;
  price: number;
  className?: string;
}

const EditBasketButton = ({
  variant = 'lg',
  hideToast = false,
  min_sell_quantity,
  available_quantity,
  hold_quantity,
  basket_quantity,
  basket_guid,
  advertisement_id,
  price,
  className = '',
}: IProps) => {
  const navigate = useNavigate();

  // UI count state (always synced with pendingQuantityRef)
  const [count, setCount] = useState(basket_quantity);

  // Store the starting quantity so we can detect "no actual change feature"
  const initialQuantityRef = useRef(basket_quantity);

  // The quantity being built up during user clicks (debounced value)
  const pendingQuantityRef = useRef(count);

  const basketGuidRef = useRef(basket_guid);

  useEffect(() => {
    basketGuidRef.current = basket_guid;
  }, [basket_guid]);

  // Track last action for toast message
  const lastActionRef = useRef<'increase' | 'decrease' | null>(null);

  // Timer for debounce API call
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Real remaining stock
  const trueQuantityAvailable =
    Math.abs((available_quantity ?? 0) - (hold_quantity ?? 0)) -
    pendingQuantityRef.current;

  const canIncrease = trueQuantityAvailable > 0 && Boolean(price);
  const canDecrease = Boolean(price);

  const trueMaxQuantity = Math.abs(
    (hold_quantity ?? 0) - (available_quantity ?? 0),
  );

  const hasMinSellQuantityIncrease = count < min_sell_quantity;

  // a flag for when the true quantity becomes less than the basket quantity user has it
  const hasQuantityUpdated = trueMaxQuantity < count;

  useEffect(() => {
    // add a fallback to stop updating count when there is not basket_guid
    if (!basketGuidRef.current) return;

    if (hasMinSellQuantityIncrease) {
      setCount(min_sell_quantity);
      editBasket({
        quantity: min_sell_quantity,
        basket_guid,
      });
    }

    if (hasQuantityUpdated) {
      setCount(trueMaxQuantity);
      editBasket({
        quantity: trueMaxQuantity,
        basket_guid,
      });
    }
  }, [basketGuidRef]);

  // Toast handler after a successful API call
  const handleSuccess = (newQuantity: number) => {
    pendingQuantityRef.current = newQuantity;
    initialQuantityRef.current = newQuantity;
    setCount(newQuantity);

    const isIncrease = lastActionRef.current === 'increase';

    !hideToast &&
      isIncrease &&
      toast.success(
        <div className="flex items-center justify-between text-md">
          <span className={isIncrease ? 'text-farm-shop-500' : 'text-red-600'}>
            {isIncrease
              ? 'محصول به سبد خرید اضافه شد'
              : 'محصول از سبد خرید کم شد'}
          </span>
          <div
            onClick={() => navigate('/shopping-basket')}
            className="px-2 py-2 text-white rounded-lg bg-farm-shop-500"
          >
            رفتن به سبد خرید
          </div>
        </div>,
      );
  };

  const handleRemoveSuccess = () => {
    pendingQuantityRef.current = 0;
    initialQuantityRef.current = 0;
    setCount(0);
    basketGuidRef.current = '';
  };

  // Create mutation (first time add)
  const {mutate: createBasket, isLoading: isCreating} = usePostBasket(() =>
    handleSuccess(min_sell_quantity),
  );

  // Update mutation (editing existing quantity)
  const {mutate: editBasket, isLoading: isEditing} = usePatchBasket(
    ({quantity}: {quantity: number}) => handleSuccess(quantity),
  );

  //Remove Mutation (remove basket from card)
  const {mutate: removeBasket, isLoading: isRemoving} =
    useDeleteBasket(handleRemoveSuccess);

  const isLoading = isCreating || isEditing || isRemoving;

  // Debounce API to avoid spamming requests on rapid user clicks. this makes it so that after 2 seconds the changes are applied
  const triggerDebouncedApi = () => {
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      const next = pendingQuantityRef.current;

      // If user returned to initial quantity → do nothing
      if (next === initialQuantityRef.current) return;

      // If basket does not exist → create it
      if (!basketGuidRef.current) {
        createBasket({
          advertisement_id,
          quantity: min_sell_quantity,
        });
        return;
      }

      // if the basket quantity is less than min_sell_quantity remove it from cart
      if (next < min_sell_quantity) {
        removeBasket(basketGuidRef.current);
        return;
      }

      // Otherwise edit existing basket
      editBasket({
        quantity: next,
        basket_guid: basketGuidRef.current,
      });
    }, 1000);
  };

  // Increase/Decrease handler
  const onAction = (action: 'increase' | 'decrease') => {
    lastActionRef.current = action;

    const next =
      action === 'increase'
        ? pendingQuantityRef.current + 1
        : Math.max(0, pendingQuantityRef.current - 1);

    pendingQuantityRef.current = next;
    // if there is basket_quantity it is a patch so the quantity will add one otherwise it is a create and the quantity is min sell quantity
    setCount(basket_quantity ? next : min_sell_quantity);

    triggerDebouncedApi();
  };

  const increase = () => {
    if (!canIncrease || isLoading) return;
    onAction('increase');
  };

  const decrease = () => {
    if (!canDecrease || isLoading) return;
    onAction('decrease');
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between text-xl cursor-pointer z-20',
        {
          'w-24 h-8 mt-3 ': variant === 'sm',
          'w-full h-9': variant === 'md',
          'w-full h-12': variant === 'lg',
        },
        className,
      )}
    >
      <div
        className={cn(
          'bg-farm-shop-500 text-white w-full h-full flex justify-center items-center gap-2 rounded-lg px-2',
          {'pointer-events-none': isLoading},
        )}
      >
        {isLoading ? (
          <SpinnerLoading size={12} />
        ) : count ? (
          <>
            <i
              className={cn('icon-namePlus-4 w-fit rounded-full', {
                'pointer-events-none text-gray-400': !canIncrease,
                'p-2': variant === 'lg',
              })}
              onClick={increase}
            />

            <span className="flex items-center justify-center w-full">
              {count}
            </span>

            <i
              className={cn('icon-nameMinus-4 w-fit rounded-full', {
                'pointer-events-none text-gray-400': !canDecrease,
                'p-2': variant === 'lg',
              })}
              onClick={decrease}
            />
          </>
        ) : (
          // Initial "Add" button UI
          <div
            onClick={increase}
            className={`${
              !canIncrease && 'pointer-events-none text-gray-400'
            } focus:scale-90 flex justify-center items-center`}
          >
            <i
              className={cn('icon-namePlus-4 ', {
                'text-[30px]': variant === 'lg',
                'text-[24px]': variant === 'sm' || variant === 'md',
              })}
            />
            <i
              className={cn('icon-nameBuy ', {
                'text-[30px]': variant === 'lg',
                'text-[24px]': variant === 'sm' || variant === 'md',
              })}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EditBasketButton;
